import React, { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { fetchProjectDetail } from '../../lib/api/projects';
import { SettingsTab } from '../../components/projects/SettingsTab';
import { ModelsTab } from '../../components/projects/ModelsTab';
import { WorkflowTab } from '../../components/projects/WorkflowTab';
import { ReposTab } from '../../components/projects/ReposTab';
import { GuardrailsTab } from '../../components/projects/GuardrailsTab';
import { AuditTab } from '../../components/projects/AuditTab';
import { GitHubEventsTab } from '../../components/projects/GitHubEventsTab';
import { SkillsTab } from '../../components/projects/SkillsTab';
import { McpServersTab } from '../../components/projects/McpServersTab';
import { ByokTab } from '../../components/projects/ByokTab';
import { AccessTab } from '../../components/projects/AccessTab';
import { SchedulesTab } from '../../components/projects/SchedulesTab';
import { ParityRunsTab } from '../../components/projects/ParityRunsTab';
import { PageHeader, StatusBadge, UrlTabs, SubNav, useUrlTab } from '../../components/ui';
import { useProjectPlan } from '../../lib/hooks/useProjectPlan';
import { PLANS } from '../../../../shared/types/plans';

type GroupId = 'overview' | 'configure' | 'agents' | 'activity';

interface SectionConfig {
  id: string;
  label: string;
  /** Uppercase label the sub-nav renders above the first item of a new group. */
  group?: string;
}

interface GroupConfig {
  id: GroupId;
  label: string;
  sections?: ReadonlyArray<SectionConfig>;
  defaultSection?: string;
}

// The project page's 13 flat tabs, grouped by the job the user is doing.
// See feature-specs/webportal-ux/UX-01-project-tabs.md for the mapping.
const GROUPS: ReadonlyArray<GroupConfig> = [
  { id: 'overview', label: 'Overview' },
  {
    id: 'configure',
    label: 'Configure',
    defaultSection: 'general',
    sections: [
      { id: 'general', label: 'General' },
      { id: 'workflow', label: 'Workflow' },
      { id: 'guardrails', label: 'Guardrails' },
      { id: 'schedules', label: 'Schedules' },
      { id: 'repos', label: 'Repos', group: 'Project' },
      { id: 'access', label: 'Access', group: 'Project' },
    ],
  },
  {
    id: 'agents',
    label: 'Agents & Models',
    defaultSection: 'models',
    sections: [
      { id: 'models', label: 'Models' },
      { id: 'keys', label: 'API keys' },
      { id: 'skills', label: 'Skills' },
      { id: 'mcp', label: 'MCP servers' },
    ],
  },
  {
    id: 'activity',
    label: 'Activity',
    defaultSection: 'audit',
    sections: [
      { id: 'audit', label: 'Audit log' },
      { id: 'github-events', label: 'GitHub activity' },
      { id: 'parity', label: 'Parity reports' },
    ],
  },
];

const GROUP_IDS = GROUPS.map((g) => g.id);

// Old flat `?tab=` ids → their new {group, section}. Rewritten once on
// mount so every link that worked before UX-01 still lands on the same
// panel, without the back button bouncing between the old and new shapes.
const LEGACY_TAB_REDIRECT: Record<string, { group: GroupId; section: string }> = {
  settings: { group: 'configure', section: 'general' },
  workflow: { group: 'configure', section: 'workflow' },
  guardrails: { group: 'configure', section: 'guardrails' },
  schedules: { group: 'configure', section: 'schedules' },
  repos: { group: 'configure', section: 'repos' },
  access: { group: 'configure', section: 'access' },
  models: { group: 'agents', section: 'models' },
  byok: { group: 'agents', section: 'keys' },
  skills: { group: 'agents', section: 'skills' },
  mcp: { group: 'agents', section: 'mcp' },
  audit: { group: 'activity', section: 'audit' },
  'github-events': { group: 'activity', section: 'github-events' },
};

export const ProjectDetailPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();

  // Legacy deep links (`?tab=settings`, `?tab=byok`, …) still worked before
  // UX-01. Rewrite them to the new {tab: group, section} shape once, on
  // mount — only the mount-time value of `?tab=` is ever a legacy id.
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const redirect = tabParam ? LEGACY_TAB_REDIRECT[tabParam] : undefined;
    if (!redirect) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', redirect.group);
    const groupConfig = GROUPS.find((g) => g.id === redirect.group);
    if (groupConfig?.defaultSection === redirect.section) nextParams.delete('section');
    else nextParams.set('section', redirect.section);
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeGroupId] = useUrlTab('tab', GROUP_IDS, 'overview');
  const activeGroup = GROUPS.find((g) => g.id === activeGroupId) ?? GROUPS[0];
  const sectionIds = activeGroup.sections?.map((s) => s.id) ?? [];
  const [activeSectionId, setActiveSectionId] = useUrlTab(
    'section',
    sectionIds,
    activeGroup.defaultSection ?? '',
  );

  const overview = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProjectDetail(id),
    enabled: id.length > 0,
  });
  // UX-06: the settings form's tier chip moved here, next to the project
  // name, since it's read-only project metadata, not something the
  // settings form itself edits. `useProjectPlan` is the canonical way to
  // read a PROJECT's (not the account's) effective plan.
  const { plan, isLoading: planLoading, isError: planError } = useProjectPlan(id);

  return (
    <div data-testid="project-detail-page" className="min-h-screen bg-slate-50">
      <Header title="Project" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Link to="/projects" className="text-xs text-primary-500 hover:text-primary-600 hover:underline">
          ← Back to projects
        </Link>

        <PageHeader
          eyebrow={<span className="font-mono">{id}</span>}
          title={overview.data?.name ?? id}
          actions={
            <>
              {!planLoading && !planError && (
                <span data-testid="project-plan-chip">
                  <StatusBadge tone="info">{PLANS[plan].label} plan</StatusBadge>
                </span>
              )}
              {overview.data?.archived ? (
                <StatusBadge tone="neutral">archived</StatusBadge>
              ) : overview.data ? (
                <StatusBadge tone="success" dot>active</StatusBadge>
              ) : null}
            </>
          }
        />

        <UrlTabs
          param="tab"
          tabs={GROUPS.map((g) => ({ id: g.id, label: g.label }))}
          defaultId="overview"
          testIdPrefix="tab"
        />

        {activeGroupId === 'overview' ? (
          <section role="tabpanel" data-testid="panel-overview" className="pt-1">
            <OverviewPanel
              isLoading={overview.isLoading}
              isError={overview.isError}
              error={overview.error as Error | null}
              data={overview.data ?? null}
              id={id}
            />
          </section>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[12rem_1fr] gap-6 pt-1">
            <SubNav
              ariaLabel={`${activeGroup.label} sections`}
              items={activeGroup.sections ?? []}
              active={activeSectionId}
              onSelect={setActiveSectionId}
            />
            <section role="tabpanel" data-testid={`panel-${activeSectionId}`}>
              {activeGroupId === 'configure' && activeSectionId === 'general' && <SettingsTab projectId={id} />}
              {activeGroupId === 'configure' && activeSectionId === 'workflow' && <WorkflowTab projectId={id} />}
              {activeGroupId === 'configure' && activeSectionId === 'guardrails' && (
                <GuardrailsTab projectId={id} />
              )}
              {activeGroupId === 'configure' && activeSectionId === 'schedules' && (
                <SchedulesTab projectId={id} />
              )}
              {activeGroupId === 'configure' && activeSectionId === 'repos' && <ReposTab projectId={id} />}
              {activeGroupId === 'configure' && activeSectionId === 'access' && <AccessTab projectId={id} />}
              {activeGroupId === 'agents' && activeSectionId === 'models' && <ModelsTab projectId={id} />}
              {activeGroupId === 'agents' && activeSectionId === 'keys' && <ByokTab projectId={id} />}
              {activeGroupId === 'agents' && activeSectionId === 'skills' && <SkillsTab projectId={id} />}
              {activeGroupId === 'agents' && activeSectionId === 'mcp' && <McpServersTab projectId={id} />}
              {activeGroupId === 'activity' && activeSectionId === 'audit' && <AuditTab projectId={id} />}
              {activeGroupId === 'activity' && activeSectionId === 'github-events' && (
                <GitHubEventsTab projectId={id} />
              )}
              {activeGroupId === 'activity' && activeSectionId === 'parity' && <ParityRunsTab projectId={id} />}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

const OverviewPanel: React.FC<{
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: Awaited<ReturnType<typeof fetchProjectDetail>> | null;
  id: string;
}> = ({ isLoading, isError, error, data, id }) => {
  if (isLoading) return <div className="text-sm text-slate-500">Loading project…</div>;
  if (isError) {
    return (
      <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
        {error?.message === 'NOT_FOUND' ? `Project "${id}" not found.` : error?.message}
      </div>
    );
  }
  if (!data) return null;
  return (
    <div className="space-y-4">
      <article className="bg-white border border-slate-200 rounded-xl shadow-card p-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <dt className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Owner</dt>
          <dd className="text-slate-900">{data.owner ?? '—'}</dd>
          <dt className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Attached repos</dt>
          <dd className="tabular-nums text-slate-900">{data.repoCount}</dd>
          <dt className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Created</dt>
          <dd className="text-slate-700">{formatTimestamp(data.createdAt)}</dd>
          <dt className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Updated</dt>
          <dd className="text-slate-700">{formatTimestamp(data.updatedAt)}</dd>
        </dl>
      </article>
    </div>
  );
};

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default ProjectDetailPage;
